import { ToolSwapAction } from './ToolSwapAction';
import {
  colorForSeat,
  colorLabel,
  GOMOKU_SIZE,
  type GomokuChoice,
  type GomokuMove,
  type GomokuState,
  isPlace,
  legalKey,
  type Side,
  stoneColor,
} from '../../games/gomoku';
import { usePendingConfirm } from '../../hooks/usePendingConfirm';
import { BoardCoords } from './BoardCoords';
import './GomokuBoard.css';

interface Props {
  state: GomokuState;
  legal?: GomokuMove[];
  /** Gomoku の yourSide は席。 black_seat による色変換はこの盤が行う。 */
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (move: GomokuMove) => void;
  mini?: boolean;
  lastMove?: GomokuMove;
  staticView?: boolean;
  suppressTurnStatus?: boolean;
}

const STARS = [[3, 3], [3, 11], [7, 7], [11, 3], [11, 11]] as const;
const CHOICE_LABEL: Record<GomokuChoice, string> = {
  TakeBlack: '黒をプレイ',
  TakeWhite: '白をプレイ',
  PlaceTwo: '4,5手目をプレイ',
};


export function GomokuBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini = false,
  lastMove,
  staticView = false,
}: Props) {
  const { pending, tap, clear } = usePendingConfirm<string>(state);
  const isMyTurn = myTurnAllowed ?? (yourSide != null && yourSide === state.side);
  const interactive = !mini && !staticView && yourSide != null && isMyTurn && state.outcome == null && onMove != null;
  const choosing = state.phase === 'FirstChoice' || state.phase === 'SecondChoice';
  const legalPlaces = new Set((legal ?? []).filter(isPlace).map((move) => legalKey(move.Place.row, move.Place.col)));
  const yourColor = yourSide == null ? null : colorForSeat(state, yourSide);
  const nextColor = stoneColor(state.stones);
  const lastKey = lastMove && isPlace(lastMove) ? legalKey(lastMove.Place.row, lastMove.Place.col) : null;
  const choices: GomokuChoice[] = state.phase === 'FirstChoice'
    ? ['TakeWhite', 'TakeBlack', 'PlaceTwo']
    : state.phase === 'SecondChoice' ? ['TakeWhite', 'TakeBlack'] : [];
  const openingStones: Side[] = state.phase === 'OpeningThree'
    ? (['First', 'Second', 'First'] as Side[]).slice(state.stones)
    : state.phase === 'ExtraTwo' ? (['Second', 'First'] as Side[]).slice(state.stones - 3) : [];

  return (
    <div className={`gomoku-frame${mini ? ' mini' : ''}`} data-testid="gomoku-frame" data-phase={state.phase} data-stones={state.stones}>
      {!mini && state.outcome && <div className="gomoku-outcome" role="status" data-testid="gomoku-phase">{state.outcome.reason === 'BoardFull' ? '盤が埋まりました — 引き分け' : `${colorLabel(state.outcome.winner_color ?? 'First')}のちょうど5連 — 終局`}</div>}
      <div className="gomoku-board" data-testid="gomoku-board" aria-label="五目並べ 15×15の盤" onClick={event => {
        if (!(event.target as Element).closest('.gomoku-intersection')) clear();
      }}>
        <div className="gomoku-grid-area">
          {!mini && <BoardCoords variant="intersection" files={Array.from({ length: GOMOKU_SIZE }, (_, c) => String.fromCharCode(65 + c))} ranks={Array.from({ length: GOMOKU_SIZE }, (_, r) => `${GOMOKU_SIZE - r}`)} />}
          <svg className="gomoku-grid-svg" viewBox="0 0 14 14" preserveAspectRatio="none" aria-hidden>
            {Array.from({ length: GOMOKU_SIZE }, (_, i) => (
              <g key={i}>
                <line x1={0} y1={i} x2={14} y2={i} />
                <line x1={i} y1={0} x2={i} y2={14} />
              </g>
            ))}
            {STARS.map(([row, col]) => <circle key={legalKey(row, col)} cx={col} cy={row} r={0.12} />)}
          </svg>
          {state.board.flatMap((row, r) => row.map((cell, c) => {
            const key = legalKey(r, c);
            const enabled = interactive && !choosing && cell == null && legalPlaces.has(key);
            const selected = enabled && pending === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`gomoku-cell-${r}-${c}`}
                data-color={cell ?? 'Empty'}
                data-pending={selected ? 'true' : undefined}
                className={`gomoku-intersection${cell === 'First' ? ' black' : cell === 'Second' ? ' white' : ''}${enabled ? ' legal' : ''}${!mini && lastKey === key ? ' last-move' : ''}${selected ? ` pending-${nextColor === 'First' ? 'black' : 'white'}` : ''}`}
                style={{ left: `${c / 14 * 100}%`, top: `${r / 14 * 100}%` }}
                disabled={!enabled}
                aria-label={`row ${r} col ${c}${cell == null ? '' : ` ${colorLabel(cell)}石`}`}
                aria-pressed={selected}
                onClick={() => tap(key, enabled, () => onMove?.({ Place: { row: r, col: c } }))}
              >
                {cell != null && <span className="gomoku-stone" aria-hidden />}
                {selected && <span className="gomoku-pending-stone" aria-hidden />}
              </button>
            );
          }))}
        </div>
      </div>
      {interactive && choices.length > 0 && (
        <div className="gomoku-choices tool-swap-row" role="group" data-testid="gomoku-choices" aria-label="Swap2の選択" data-color="Unassigned">
          {choices.map((choice) => (
            <ToolSwapAction key={choice} bowlAsset tool={choice === 'TakeBlack' ? 'black' : choice === 'TakeWhite' ? 'white' : 'pair'}
              label={CHOICE_LABEL[choice]}
              testId={`gomoku-${choice === 'TakeBlack' ? 'take-black' : choice === 'TakeWhite' ? 'take-white' : 'place-two'}`}
              disabled={!(legal ?? []).includes(choice)}
              onClick={() => { if (interactive && (legal ?? []).includes(choice)) { clear(); return onMove?.(choice); } }} />
          ))}
        </div>
      )}
      {!mini && !staticView && !state.outcome && openingStones.length > 0 && <div className="gomoku-opening-stones" data-testid="gomoku-opening-stones" role="img" aria-label={`これから置く石：${openingStones.map(colorLabel).join('、')}`}>
        {openingStones.map((color, index) => <span key={`${state.stones + index}`} className={`gomoku-opening-stone ${color === 'First' ? 'black' : 'white'}`} data-color={color} aria-hidden="true" />)}
      </div>}
      {!mini && !staticView && state.phase === 'Main' && yourColor != null && <div className="gomoku-own-tools" data-testid="gomoku-own-tools" data-color={yourColor} role="img" aria-label={`あなたの${colorLabel(yourColor)}の碁笥`}>
        <img src={`/generated/vessels/goke-rim-${yourColor === 'First' ? 'black' : 'white'}.png`} alt="" draggable={false} />
      </div>}

    </div>
  );
}

export const GOMOKU_BOARD_SIZE = GOMOKU_SIZE;
